import {
  getDeleteApiCertificationProgramIdMutationOptions,
  type CertificationProgramListItemDTO,
} from '@smartsash/api-client';
import { usePermission } from '@smartsash/permissions';
import { useEntityDelete } from '@smartsash/server-ops';
import { Icon, openModal } from '@smartsash/ui';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { RowActions as Shared, type RowActionEntry } from '@/ui/shared/RowActions';
import { certificationProgramName } from '@/utils/certification/certificationProgramName';

/**
 * HS-1162 restores the delete action that HS-847 removed.
 *
 * It was removed for a good reason: the screen was mock, so the action deleted an id that did not
 * exist, and the service behind it issued `DELETE /api/Certification/questions/{id}` while
 * documenting `DELETE /api/Certification/{id}` — against a real backend it deleted a *question*
 * carrying that id. Both problems are gone. The row is a real `CertificationProgram` and the
 * mutation is the generated one for that resource, which cascades to the programme's items,
 * answers and stored video files.
 *
 * The action is gated on `CertificationProgram.Manage` — a show/hide check only; the server
 * enforces the same key through `[AuthorizePermission]`.
 */
function RowActions({ row }: { row: CertificationProgramListItemDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const id = row.id ?? '';
  const canManage = can('certificationProgram.delete');
  const del = useEntityDelete({
    entity: 'certificationProgram',
    mutationOptions: getDeleteApiCertificationProgramIdMutationOptions,
  });

  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('certifications.deleteTitle', {
        name: certificationProgramName(row, row.institutionName ?? ''),
      }),
      description: t('certifications.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ id });
  }, [del, id, row, t]);

  const items: RowActionEntry[] = [
    // DV-01: opening a programme now means reading it. Editing stays one entry below, and is the
    // only one of the two that needs `CertificationProgram.Manage` on the server.
    {
      label: t('common.view'),
      icon: 'eye',
      onClick: () => {
        void navigate({ to: '/certifications/$id', params: { id } });
      },
    },
    {
      label: t('common.edit'),
      icon: 'edit-01',
      // HS-1396: Delete was already gated through the `canManage` spread below, but Edit was not —
      // so a read-only session was offered a button onto `/certifications/$id/edit`, a route whose
      // own guard requires this very key and would have refused it.
      permission: 'certificationProgram.edit',
      onClick: () => {
        void navigate({ to: '/certifications/$id/edit', params: { id } });
      },
    },
    null,
    // Institution-scoped programmes (HS-1366) have no laboratory to open.
    ...(row.labId == null
      ? []
      : ([
          {
            label: t('certifications.actions.viewLaboratory'),
            icon: 'lab',
            // The row carries the laboratory's own id, so this opens that exact record rather
            // than searching the list by name — a name search can miss it (rename, punctuation)
            // or match more than one lab, where the id never can.
            onClick: () => {
              void navigate({ to: '/laboratories/$id', params: { id: row.labId ?? '' } });
            },
          },
        ] satisfies RowActionEntry[])),
    {
      label: t('certifications.actions.viewInstitute'),
      icon: 'building-07',
      onClick: () => {
        void navigate({ to: '/institutions/$id', params: { id: row.institutionId ?? '' } });
      },
    },
    ...(canManage
      ? ([
          null,
          {
            label: t('common.delete'),
            icon: 'trash-01',
            onClick: () => {
              void handleDelete();
            },
          },
        ] satisfies RowActionEntry[])
      : []),
  ];

  return <Shared items={items} />;
}

export default RowActions;
